/**
 * Post-install.
 *
 * **Partly inferred, and the reason is worth reading before trusting this schema.**
 *
 * There *is* a live Qualtrics form called `WW Post Install Form` (`SV_4NI8bam7NsojZNY`, 43
 * questions), and its enabled workflow inserts `Sim_Ticket__c` under record type
 * `012at000000V3EXAA0`, whose object label is literally "Post Install", across 56 mapped fields.
 * That much is verified. But reading its questions makes clear it is **host-facing, not
 * technician-facing**: "What do you need assistance with?", "I need to change the store
 * name/address that is shown on the Amazon website", "Would you be willing to conduct any site
 * preparation if necessary?", "Contact Details (With correct formatting)". It is an intake form a
 * store manager fills in to raise a request. It creates the Post Install record that later
 * technician visits then reference by passing its id as the `PI` / `PIID` parameter.
 *
 * So the technician-facing post-install visit form does not exist in Qualtrics in the shape this
 * app needs. What does exist, and is genuinely technician- or supplier-facing:
 *
 *  - **`Post Install - Site Prep Verification`** (`SV_6fHKE0FNgpKZNxI`, 55 responses, 3 questions).
 *    Sourced verbatim below. It updates one field, `Sim_Ticket__c.Site_Prep_Completion_Date__c`,
 *    and posts to Chatter.
 *  - **Module-change wording** from `WW Post Install Form` `Q50` / `Q58` / `Q55` / `Q54` — the
 *    adder-and-quarter counting, the "does your locker have a screen" gate, the module guide.
 *    Reused here because it is the vocabulary that already exists for describing a module change,
 *    even though the source asks it of a host rather than a technician.
 *
 * Everything else — the work-performed section, the host sign-off, the resulting-layout
 * confirmation — is **inferred**. It is a reasonable shape for the visit and it is not sourced.
 * Treat it as a proposal to review with the owner, not as a port.
 *
 * One structural consequence of the destination: this form writes `Sim_Ticket__c`, not
 * `Work_Submission__c`, so almost none of the field mappings that were verified for the other four
 * schemas apply. `Sim_Ticket__c` mappings below are name-matched against its describe (464 fields);
 * the 56-field WW Post Install workflow mapping was captured but is for host-facing questions that
 * do not correspond to these.
 */

import type { FormSchema } from '../types.ts';

const ST = 'Sim_Ticket__c';
const DESCRIBE = 'discovery/salesforce/field-dumps.txt (Sim_Ticket__c describe, 464 fields)';

function described(field: string, type: string, caveat?: string) {
  return {
    object: ST,
    field,
    type,
    confidence: 'verified' as const,
    evidence: `${DESCRIBE}. Field exists; that this question writes it is not observed.`,
    ...(caveat !== undefined ? { caveat } : {}),
  };
}

function guess(field: string, why: string) {
  return { object: ST, field, confidence: 'unverified' as const, evidence: `No describe hit. ${why}` };
}

const MODULE_CHANGE = {
  field: 'change_type',
  op: 'in' as const,
  value: ['Modules added', 'Modules removed'],
};

export const POST_INSTALL: FormSchema = {
  id: 'post-install',
  version: 1,
  title: 'Post-install',
  source: {
    provenance: 'partly-inferred',
    artefact:
      'Post Install - Site Prep Verification — Qualtrics SV_6fHKE0FNgpKZNxI (3 questions, sourced verbatim) + module-change wording from WW Post Install Form SV_4NI8bam7NsojZNY Q50/Q54/Q55/Q58 (host-facing, reused) + inferred visit close-out',
    sourceQuestionCount: 3,
    note:
      'The only fully sourced part is the three-question site-prep verification. WW Post Install Form is verified as the writer of the Post Install Sim_Ticket__c record type, but its 43 questions are host-facing intake, not a technician visit form — so it establishes the destination and some vocabulary, not the questions. Everything marked inferred is a proposal, not a port, and should be reviewed with the owner before it is relied on.',
  },
  destination: {
    object: 'Sim_Ticket__c',
    recordTypeId: '012at000000V3EXAA0',
    recordTypeLabel: 'Post Install',
    confidence: 'verified',
    evidence:
      'discovery/qualtrics/10-WORKFLOWS-AND-SF-MAPPING.md — WW Post Install Form inserts Sim_Ticket__c with RecordTypeId 012at000000V3EXAA0 (object label "Post Install") across 56 fields, stamping Post_Install_Source__c = "Post Install Form". Note this is an *update* path for a technician visit, not an insert: the Post Install record already exists and this visit patches it. Four other surveys already update rather than insert it.',
  },
  sections: [
    {
      id: 'visit',
      title: 'This visit',
      fields: [
        {
          id: 'kiosk_id',
          kind: 'text',
          label: 'Enter the Kiosk ID:',
          required: true,
          prefill: { source: 'kiosk.id' },
          maps: described('Kiosk_ID__c', 'string(100), externalId + idLookup'),
        },
        {
          id: 'locker_name',
          kind: 'text',
          label: 'Locker name',
          required: true,
          prefill: { source: 'kiosk.name' },
          maps: described('Kiosk_Name__c', 'string(100)', 'Field label is "Kiosk Name From Intake". Kiosk_Name_Lookup__c is the formula twin and is read-only.'),
        },
        {
          id: 'piid',
          kind: 'text',
          label: 'Enter the PIID (Post Install ID):',
          help: 'The record this visit patches. It is a Sim_Ticket__c id, prefix a4Y, which is why technicians call it a PIID.',
          required: true,
          prefill: { source: 'intervention.postInstallId', overridable: false },
          maps: described('Id', 'Id', 'The target record id, not a field to write. Carried so the backend knows which record to patch.'),
        },
        {
          id: 'location_name',
          kind: 'text',
          label: 'Location name',
          prefill: { source: 'kiosk.siteName' },
          maps: described('Location_Name__c', 'string(225)'),
        },
        {
          id: 'occupancy',
          kind: 'single',
          label:
            'Residential includes apartment style buildings. Retail includes commercial businesses, universities, etc. where the locker is open to the public. Please confirm below.',
          options: ['Apartment Locker (Hub)', 'Retail Locker'],
          required: true,
          prefill: { source: 'intervention.lockerType', overrideHint: 'The record holds Commercial or Residential; this field uses different words for the same thing.' },
          sourceTag: 'WW Post Install Q1',
          help:
            'Wording is from the host-facing form, which shows the record\'s value and asks for confirmation — the same confirm-and-correct pattern this app uses everywhere.',
          maps: described('Locker_Type__c', 'picklist Apartment Locker (Hub)|Retail Locker', 'A third vocabulary for the same concept: OpportunityLineItem says Commercial/Residential, Work_Submission__c says Commercial/Residential among nine values, and this object says Apartment Locker (Hub)/Retail Locker. Prefill must map across all three.'),
        },
      ],
    },

    {
      id: 'site-prep-verification',
      title: 'Site prep verification',
      subtitle: 'Sourced verbatim from Post Install - Site Prep Verification (SV_6fHKE0FNgpKZNxI).',
      fields: [
        {
          id: 'site_prep_confirmed',
          kind: 'yesno',
          label: 'Please confirm that you have completed site prep as noted on your email.',
          required: true,
          sourceTag: 'confirmation',
          maps: described('PI_SPV_status__c', 'picklist SPV not needed|Ready for Ops Review|Need more info|SPV completed', 'A yes/no answer against a four-value status picklist. A mapping rule is needed and does not exist: "Yes" is plausibly "SPV completed" and "No" is plausibly "Need more info", but that is a guess.'),
        },
        {
          id: 'site_prep_photos',
          kind: 'photo',
          label: 'Please provide any relevant pictures of the site prep location.',
          showIf: { field: 'site_prep_confirmed', op: 'eq', value: true },
          validate: { maxPhotos: 6 },
          sourceTag: 'Q2',
          maps: guess('PI_Site_Prep_Photo__c', 'The source survey attaches these as an Attachment / ContentVersion rather than writing a field. Photos on this path are attachments, not a URL field.'),
        },
        {
          id: 'site_prep_readiness_date',
          kind: 'date',
          label: 'Estimated Readiness Date For Site Prep',
          required: true,
          showIf: { field: 'site_prep_confirmed', op: 'eq', value: false },
          prefill: { source: 'sitePrep.readinessDate', overrideHint: 'Revise it if the date has moved.' },
          sourceTag: 'Q3',
          maps: described('PI_Supplier_Site_Prep_readiness__c', 'date', 'The source survey writes Site_Prep_Completion_Date__c, which the describe excerpt reviewed does not list; the closest confirmed names are Supplier_Site_Prep_Completion_Date__c and PI_Supplier_Site_Prep_readiness__c. A readiness *estimate* is the latter, a *completion* is the former, and the source conflates them.'),
        },
      ],
    },

    {
      id: 'work',
      title: 'What changed',
      subtitle: 'Inferred. Not a port of any existing form — review before relying on it.',
      fields: [
        {
          id: 'change_type',
          kind: 'single',
          label: 'What was the post-install change?',
          options: [
            'Modules added',
            'Modules removed',
            'Locker relocated on site',
            'Cosmetic / branding correction',
            'Snag list closed out',
            'Site prep verification only',
          ],
          required: true,
          inferred: true,
          maps: described('Reason_for_request__c', 'picklist (~80 values)', 'The object has an 80-value reason picklist that these six would need mapping into. That map does not exist yet.'),
        },
        {
          id: 'has_screen',
          kind: 'yesno',
          label: 'Does your locker have a screen?',
          required: true,
          showIf: MODULE_CHANGE,
          sourceTag: 'WW Post Install Q55',
          help: 'From the host-facing form, where it gates the upsize guide. A screenless locker counts modules differently.',
          maps: guess('Has_Screen__c', 'No screen-presence field found in the describe.'),
        },
        {
          id: 'adders_changed',
          kind: 'number',
          label: 'How many Adder (A) modules, with 23 slots each?',
          required: true,
          showIf: { all: [MODULE_CHANGE, { field: 'has_screen', op: 'eq' as const, value: true }] },
          validate: { integer: true, min: 0, max: 20 },
          sourceTag: 'WW Post Install Q50',
          maps: described('Modules_Changed__c', 'string(255)', "The source workflow builds this as a formatted string: \"A's/M's: <n>  Q's: <m>\". Two numeric answers are concatenated into one text field, so the counts cannot be queried. Carried faithfully, and worth fixing."),
        },
        {
          id: 'quarters_changed',
          kind: 'number',
          label: 'How many Quarter (Q) modules, with 3 slots each?',
          required: true,
          showIf: { all: [MODULE_CHANGE, { field: 'has_screen', op: 'eq' as const, value: true }] },
          validate: { integer: true, min: 0, max: 20 },
          sourceTag: 'WW Post Install Q50',
          maps: described('Modules_Changed__c', 'string(255)', 'Same concatenated field as adders_changed. Both answers land in one string.'),
        },
        {
          id: 'modules_changed_screenless',
          kind: 'number',
          label: 'How many 3ft / 0.9m modules, with 23 slots each?',
          required: true,
          showIf: { all: [MODULE_CHANGE, { field: 'has_screen', op: 'eq' as const, value: false }] },
          validate: { integer: true, min: 0, max: 20 },
          sourceTag: 'WW Post Install Q58',
          maps: described('Lockers_Expanded_Downsized__c', 'string(200)'),
        },
        {
          id: 'serials_note',
          kind: 'note',
          label:
            'Serials for modules added or removed are captured on the module screen with their positions, not typed here. That is the point of the asset work: a module change should update the asset records, not only a text field on a ticket.',
          showIf: MODULE_CHANGE,
        },
        {
          id: 'relocation_placement',
          kind: 'single',
          label: 'Where did you move the locker to?',
          options: ['Indoor', 'Outdoor'],
          required: true,
          showIf: { field: 'change_type', op: 'eq', value: 'Locker relocated on site' },
          sourceTag: 'WW Post Install Q28',
          maps: described('Relocation_Indoor_Outdoor__c', 'picklist Indoor|Outdoor'),
        },
        {
          id: 'relocation_type',
          kind: 'single',
          label: 'What kind of relocation was it?',
          options: ['Indoor to Indoor', 'Outdoor to Outdoor', 'Indoor to Outdoor', 'Outdoor to Indoor'],
          required: true,
          showIf: { field: 'change_type', op: 'eq', value: 'Locker relocated on site' },
          inferred: true,
          help: 'Derivable from the record\'s current placement and the answer above, but the field exists and the derivation would be silent, so it is asked.',
          maps: described('Relocation_Type__c', 'picklist (4 values)'),
        },
        {
          id: 'resulting_layout',
          kind: 'text',
          label: 'Enter the resulting locker layout (S / A / Q / M):',
          required: true,
          showIf: { not: { field: 'change_type', op: 'eq', value: 'Site prep verification only' } },
          prefill: { source: 'kiosk.layout', overrideHint: 'This is the layout as you are leaving it, so correcting it is the normal case after a module change.' },
          inferred: true,
          validate: { minLength: 1, maxLength: 30 },
          maps: described('New_PI_Layout__c', 'string(30)'),
        },
        {
          id: 'after_photo',
          kind: 'photo',
          label: 'Photo of the front of the locker after the change',
          required: true,
          showIf: { not: { field: 'change_type', op: 'eq', value: 'Site prep verification only' } },
          inferred: true,
          maps: described('Please_upload_a_photo_of_damage__c', 'textarea(32768)', 'Wrong field for the purpose — it is the damage-photo field. No general after-photo field was found on this object. Flagged rather than silently reused.'),
        },
      ],
    },

    {
      id: 'signoff',
      title: 'Sign-off',
      subtitle: 'Inferred.',
      fields: [
        {
          id: 'host_signed_off',
          kind: 'single',
          label: 'Did the host sign off on the completed work?',
          options: ['Yes', 'No', 'No host present'],
          required: true,
          inferred: true,
          maps: guess('Host_Signed_Off__c', 'No sign-off field found in the describe.'),
        },
        {
          id: 'host_objection',
          kind: 'textarea',
          label: 'What did the host object to?',
          required: true,
          showIf: { field: 'host_signed_off', op: 'eq', value: 'No' },
          inferred: true,
          validate: { maxLength: 3000 },
          maps: described('Supplier_Site_Prep_Rework_Needed__c', 'textarea(32768)', 'Repurposed; the object has no general objection field.'),
        },
        {
          id: 'comments',
          kind: 'textarea',
          label: 'Anything else the next person needs to know?',
          inferred: true,
          validate: { maxLength: 3000 },
          maps: described('Locker_Address__c', 'textarea(32768)', 'Wrong field. There is no free-comment field on the Post Install record type that was confirmed. This answer currently has nowhere correct to land.'),
        },
      ],
    },
  ],
};
